'use client';

import { useState } from 'react';
import { useLanguage } from '../../../../utils/i18n';
import {
  Box,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Chip,
  CircularProgress,
  TablePagination,
  Menu,
  MenuItem,
  Select
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { useTireHistoricalList, exportTireHistoricalList } from '../../../../stores/tireHistoricalStore';
import { useTireBatchListAll } from '../../../../stores/tireBatchStore';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import TransactionDetailModal from './TransactionDetailModal';

const getFormattedDate = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

export const getActionStyles = (action) => {
  if (!action) return { bg: '#f1f5f9', color: '#475569' };
  const lower = action.toLowerCase();
  switch (lower) {
    case 'fit':
      return { bg: '#dcfce7', color: '#166534' }; // Pastel Green
    case 'remove':
      return { bg: '#fee2e2', color: '#991b1b' }; // Pastel Red
    case 'repair':
      return { bg: '#fef3c7', color: '#92400e' }; // Pastel Orange
    default:
      return { bg: '#f1f5f9', color: '#475569' };
  }
};

export const findPositionLabel = (posData, sn) => {
  if (!posData?.axle_info?.info) return '-';
  for (const axle of posData.axle_info.info) {
    if (axle.positions) {
      for (const pos of axle.positions) {
        if (pos.serial_number === sn) {
          return pos.label || pos.code || '-';
        }
      }
    }
  }
  return '-';
};

export const HtmlTableContainer = styled('div')({
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  border: '1px solid #e2e8f0',
  borderBottom: 'none',
  borderRadius: '8px 8px 0 0',
  backgroundColor: '#ffffff'
});

export const HtmlTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  textAlign: 'left'
});

export const StyledTableCell = styled('th')(({ theme }) => ({
  fontWeight: 400,
  fontSize: '0.875rem',
  color: '#ffffff',
  backgroundColor: '#5166AE',
  borderBottom: '2px solid #e2e8f0',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  zIndex: 1
}));

export const StyledTableRow = styled('tr')(({ theme }) => ({
  backgroundColor: '#ffffff',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#f1f5f9'
  },
  '& td': {
    padding: '8px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    textAlign: 'left'
  }
}));

export default function TireHistoricalPage() {
  const { t } = useLanguage();

  const EXPORT_COLUMNS = [
    { field: 'serial_number', headerName: t('tireRepairScrap.serialNumberExport', 'Serial Number') },
    { field: 'transaction_date', headerName: t('tireRepairScrap.date', 'Date') },
    { field: 'transaction_type', headerName: t('tireCardHistory.actionType', 'Action Type') },
    { field: 'tire_spec_name', headerName: t('specification.spec_name', 'Spec Name') },
    { field: 'tire_brand_name', headerName: t('tireRepairScrap.brandExport', 'Brand') },
    { field: 'tire_size_code_internal', headerName: t('tireRepairScrap.sizeExport', 'Size') },
    { field: 'license_plate', headerName: t('tireCardHistory.vehicle', 'Vehicle') },
    { field: 'km_unit', headerName: t('tireCardHistory.kmUnit', 'KM Unit') },
    { field: 'hm_unit', headerName: t('tireCardHistory.hmUnit', 'HM Unit') },
    { field: 'current_position', headerName: t('tireCycle.position', 'Position') },
    { field: 'air_pressure', headerName: 'Air Pressure (psi)' },
    { field: 'tread_depth', headerName: 'Tread Depth (mm)' },
    { field: 'mechanic_name', headerName: t('tireCardHistory.mechanicName', 'Mechanic Name') },
    { field: 'tire_damage_name', headerName: t('tireCardHistory.tireDamage', 'Tire Damage') },
    { field: 'cost_repair', headerName: t('tireCardHistory.costRepair', 'Cost Repair') },
    { field: 'vendor_name', headerName: t('tireCardHistory.vendorName', 'Vendor Name') },
    { field: 'work_order_number', headerName: t('tireCardHistory.workOrder', 'Work Order') },
    { field: 'vendor_type', headerName: t('tireCardHistory.vendorType', 'Vendor Type') },
    { field: 'repair_type', headerName: t('tireCardHistory.repairType', 'Repair Type') },
    { field: 'notes', headerName: t('tireCardHistory.notes', 'Notes') },
    { field: 'action_status', headerName: t('tireCardHistory.actionStatus', 'Action Status') },
    { field: 'final_rtd', headerName: 'Final RTD (mm)' },
    { field: 'reason', headerName: t('tireCardHistory.reason', 'Reason') }
  ];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchColumn, setSearchColumn] = useState('all');
  const [searchInput, setSearchInput] = useState('');

  const [dateFilter, setDateFilter] = useState('To Day');
  const [customDate, setCustomDate] = useState({
    startDate: dayjs().subtract(7, 'day'),
    endDate: dayjs()
  });

  const [selectedCompanyValues, setSelectedCompanyValues] = useState(() => {
    try {
      const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          return [String(parsed.company || parsed.company_id)];
        }
      }
      const rawCompany = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (rawCompany) return [String(rawCompany)];
    } catch (e) {
      console.log(e);
    }
    return [];
  });
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const [selectedSerialNumberValues, setSelectedSerialNumberValues] = useState([]);

  const serialNumberPickerParams = usePickerPagination({
    fetchHook: useTireBatchListAll,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      selectedCompanyValues.map((v) => ({ value: v })),
      state.searchKey,
      state.searchValue
    ],
    searchKey: 'serial_number',
    formatOption: (item) => ({
      ...item,
      id: item.serial_number,
      value: item.serial_number,
      label: item.serial_number
    })
  });

  const [appliedFilters, setAppliedFilters] = useState(() => {
    let initialCompany = [];
    try {
      const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          initialCompany = [String(parsed.company || parsed.company_id)];
        }
      }
      const rawCompany = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (rawCompany && initialCompany.length === 0) initialCompany = [String(rawCompany)];
    } catch (e) {
      console.log(e);
    }
    return {
      companies: initialCompany,
      serialNumbers: [],
      dateFilter: 'To Day',
      customDate: { startDate: dayjs().subtract(7, 'day'), endDate: dayjs() },
      searchColumn: 'all',
      searchQuery: ''
    };
  });

  const [hasSearched, setHasSearched] = useState(false);

  const handleSearchClick = () => {
    setAppliedFilters({
      companies: selectedCompanyValues,
      serialNumbers: selectedSerialNumberValues.map((v) => String(v)),
      dateFilter,
      customDate,
      searchColumn,
      searchQuery: searchInput
    });
    setHasSearched(true);
    setPage(0);
  };

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportOpen = Boolean(exportAnchorEl);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const getDateRange = () => {
    let startDate, endDate;
    switch (appliedFilters.dateFilter) {
      case 'To Day':
        startDate = dayjs().format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Last Week':
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Last Month':
        startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Custom':
        startDate = appliedFilters.customDate.startDate.format('YYYY-MM-DD');
        endDate = appliedFilters.customDate.endDate.format('YYYY-MM-DD');
        break;
      default:
        startDate = dayjs().format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
    }
    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();
  const companiesArg = appliedFilters.companies.map((val) => ({ value: val }));
  const serialNumbersArg = appliedFilters.serialNumbers ? appliedFilters.serialNumbers.map((val) => ({ value: val })) : [];
  const dateRangeArg = startDate && endDate ? { startDate, endDate } : null;

  const { data, isLoading, totalRecord } = useTireHistoricalList(
    page,
    rowsPerPage,
    appliedFilters.searchColumn,
    appliedFilters.searchQuery,
    companiesArg,
    dateRangeArg,
    serialNumbersArg
  );

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const getExportCellValue = (row, field) => {
    switch (field) {
      case 'transaction_date':
        return row.transaction_date ? dayjs(row.transaction_date).format('DD MMM YYYY') : '-';
      case 'serial_number':
        return row.serial_number || '-';
      case 'transaction_type':
        return row.transaction_type || '-';
      case 'tire_spec_name':
        return row.tire_spec_name || '-';
      case 'tire_brand_name':
        return row.tire_brand_name || '-';
      case 'tire_size_code_internal':
        return row.tire_size_code_internal || row.tire_size_code || '-';
      case 'license_plate':
        return row.license_plate || '-';
      case 'km_unit':
        return row.km_unit ?? '-';
      case 'hm_unit':
        return row.hm_unit ?? '-';
      case 'current_position':
        return findPositionLabel(row.current_position, row.serial_number);
      case 'tire_damage_name':
        return row.tire_damage_name || '-';
      case 'air_pressure':
        return row.air_pressure ?? '-';
      case 'tread_depth':
        return row.tread_depth ?? '-';
      case 'mechanic_name':
        return row.mechanic_name || '-';
      case 'cost_repair':
        return row.cost_repair != null ? `Rp ${Number(row.cost_repair).toLocaleString('id-ID')}` : '-';
      case 'vendor_name':
        return row.vendor_name || '-';
      case 'work_order_number':
        return row.work_order_number || '-';
      case 'vendor_type':
        return row.vendor_type || '-';
      case 'repair_type':
        return row.repair_type || '-';
      case 'notes':
        return row.notes || '-';
      case 'action_status':
        return row.action_status || '-';
      case 'final_rtd':
        return row.final_rtd ?? '-';
      case 'reason':
        return row.reason || '-';
      default:
        return '-';
    }
  };

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireHistoricalList(
        appliedFilters.searchColumn,
        appliedFilters.searchQuery,
        companiesArg,
        totalRecord,
        dateRangeArg,
        serialNumbersArg
      );
      const formattedData = dataToExport.map((row) => {
        const rowValues = {};
        EXPORT_COLUMNS.forEach((col) => {
          rowValues[col.headerName] = getExportCellValue(row, col.field);
        });
        return rowValues;
      });
      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `historical-tire-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireHistoricalList(
        appliedFilters.searchColumn,
        appliedFilters.searchQuery,
        companiesArg,
        totalRecord,
        dateRangeArg,
        serialNumbersArg
      );
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('History');
      worksheet.columns = EXPORT_COLUMNS.map((col) => ({ header: col.headerName, key: col.field, width: 20 }));

      dataToExport.forEach((row) => {
        const rowValues = {};
        EXPORT_COLUMNS.forEach((col) => {
          rowValues[col.field] = getExportCellValue(row, col.field);
        });
        worksheet.addRow(rowValues);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: 'center' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `historical-tire-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireHistoricalList(
        appliedFilters.searchColumn,
        appliedFilters.searchQuery,
        companiesArg,
        totalRecord,
        dateRangeArg
      );
      const doc = new jsPDF('landscape');
      doc.setFontSize(14);
      doc.text(`Tire Historical Transactions`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 22);

      const tableData = dataToExport.map((row) => EXPORT_COLUMNS.map((col) => getExportCellValue(row, col.field)));

      autoTable(doc, {
        head: [EXPORT_COLUMNS.map((col) => col.headerName)],
        body: tableData,
        startY: 30,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [25, 118, 210] }
      });

      doc.save(`historical-tire-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            {t('tireHistorical.title', 'Tire Historical')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tireHistorical.description', 'View all tire historical transactions with detailed information.')}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            disabled={isExporting || !hasSearched || !data || data.length === 0}
            sx={{ bgcolor: '#ffffff', whiteSpace: 'nowrap' }}
          >
            {isExporting ? t('tireCardHistory.exporting', 'Exporting...') : t('tireCardHistory.export', 'Export')}
          </Button>
          <Menu anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
            <MenuItem onClick={exportToCSV}>{t('tireCardHistory.exportCsv', 'Export to CSV')}</MenuItem>
            <MenuItem onClick={exportToExcel}>{t('tireCardHistory.exportExcel', 'Export to Excel')}</MenuItem>
            <MenuItem onClick={exportToPDF}>{t('tireCardHistory.exportPdf', 'Export to PDF')}</MenuItem>
          </Menu>
        </Box>
      </Stack>

      <Box sx={{ bgcolor: '#ffffff', p: 1, borderRadius: 2, border: '1px solid #e2e8f0', mb: 1 }}>
        <Stack
          direction={{ xs: 'column', xl: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', xl: 'center' }}
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap width={{ xs: '100%', xl: 'auto' }}>
            {/* Filter Company */}
            <Box sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <PickerFieldTable
                {...companyPickerParams}
                multiSelect
                label={t('tireHistorical.company', 'Company')}
                placeholder={t('selectCompany', 'Select Company...')}
                selectedItems={(selectedCompanyValues || []).map((v) => ({ id: v, value: v }))}
                value={selectedCompanyValues}
                onChange={(newVals, newItems) => {
                  setSelectedCompanyValues(newVals || []);
                }}
                columns={[{ field: 'name', headerName: t('companyName', 'Company Name'), renderCell: (row) => row.raw?.name || row.label }]}
                getOptionId={(opt) => String(opt.id || opt.value)}
                getOptionLabel={(opt) => opt.name || opt.label}
                searchPlaceholder={t('searchCompany', 'Search by Company Name...')}
              />
            </Box>

            {/* Filter Serial Number */}
            <Box sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <PickerFieldTable
                {...serialNumberPickerParams}
                multiSelect
                label={t('tireRepairScrap.serialNumberExport', 'Serial Number')}
                placeholder={t('selectSerialNumber', 'Select Serial Number...')}
                selectedItems={(selectedSerialNumberValues || []).map((v) => ({ id: v, value: v }))}
                value={selectedSerialNumberValues}
                onChange={(newVals) => {
                  setSelectedSerialNumberValues(newVals || []);
                }}
                columns={[
                  { field: 'serial_number', headerName: t('tireRepairScrap.serialNumberExport', 'Serial Number') },
                  {
                    field: 'tire_brand_name',
                    headerName: t('tireRepairScrap.brandExport', 'Brand'),
                    renderCell: (r) => r.join?.tire_item_tire_brand_name || '-'
                  },
                  {
                    field: 'tire_size_code',
                    headerName: t('tireRepairScrap.sizeExport', 'Size'),
                    renderCell: (r) => r.join?.tire_item_tire_size_code_internal || r.join?.tire_item_tire_size_code || '-'
                  },
                  {
                    field: 'pattern',
                    headerName: t('specification.pattern', 'Pattern'),
                    renderCell: (r) => r.join?.m_tire_spec_pattern || '-'
                  },
                  {
                    field: 'air_pressure',
                    headerName: t('tireCycle.airPressureTh', 'Air Pressure'),
                    renderCell: (r) => {
                      if (r.last_inspection?.air_pressure)
                        return `${r.last_inspection.air_pressure} (${t('tireHistorical.lastInspection', 'last inspection')})`;
                      if (r.air_pressure) return `${r.air_pressure} (${t('detailTireInventory.base', 'base').toLowerCase()})`;
                      if (r.join?.m_tire_spec_pressure_std)
                        return `${r.join.m_tire_spec_pressure_std} (${t('detailTireInventory.standard', 'standard').toLowerCase()})`;
                      return '-';
                    }
                  },
                  {
                    field: 'tread_depth',
                    headerName: t('tireCycle.treadDepthTh', 'Tread Depth'),
                    renderCell: (r) => {
                      if (r.last_inspection?.tread_depth)
                        return `${r.last_inspection.tread_depth} (${t('tireHistorical.lastInspection', 'last inspection')})`;
                      if (r.tread_depth) return `${r.tread_depth} (${t('detailTireInventory.base', 'base').toLowerCase()})`;
                      if (r.join?.m_tire_spec_original_tread_depth)
                        return `${r.join.m_tire_spec_original_tread_depth} (${t('detailTireInventory.standard', 'standard').toLowerCase()})`;
                      return '-';
                    }
                  },
                  { field: 'last_position', headerName: t('detailTireInventory.lastPosition', 'Last Position') },
                  { field: 'last_vehicle', headerName: t('tireHistorical.lastVehicle', 'Last Vehicle') }
                ]}
                getOptionId={(opt) => opt?.serial_number || opt?.value || opt}
                searchPlaceholder={t('searchBySerialNumber', 'Search by Serial Number...')}
              />
            </Box>

            {/* Filter Date */}
            <Select
              size="small"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              sx={{ minWidth: 140, bgcolor: 'white', height: 40 }}
            >
              <MenuItem value="To Day">{t('tireRepairScrap.today', 'To Day')}</MenuItem>
              <MenuItem value="Last Week">{t('tireRepairScrap.lastWeek', 'Last Week')}</MenuItem>
              <MenuItem value="Last Month">{t('tireRepairScrap.lastMonth', 'Last Month')}</MenuItem>
              <MenuItem value="Custom">{t('tireRepairScrap.custom', 'Custom')}</MenuItem>
            </Select>

            {dateFilter === 'Custom' && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('startDate', 'Start Date')}
                  value={customDate.startDate}
                  onChange={(newDate) => setCustomDate({ ...customDate, startDate: newDate })}
                  disableFuture
                  slotProps={{
                    textField: { size: 'small', sx: { width: 140, bgcolor: 'white', height: 40, '& .MuiInputBase-root': { height: 40 } } }
                  }}
                />
                <DatePicker
                  label={t('endDate', 'End Date')}
                  value={customDate.endDate}
                  onChange={(newDate) => setCustomDate({ ...customDate, endDate: newDate })}
                  minDate={customDate.startDate}
                  disableFuture
                  slotProps={{
                    textField: { size: 'small', sx: { width: 140, bgcolor: 'white', height: 40, '& .MuiInputBase-root': { height: 40 } } }
                  }}
                />
              </LocalizationProvider>
            )}

            {/* Search */}
            <Select
              size="small"
              value={searchColumn}
              onChange={(e) => {
                setSearchColumn(e.target.value);
                setSearchInput('');
              }}
              sx={{ minWidth: 150, bgcolor: 'white', height: 40 }}
            >
              <MenuItem value="all">{t('tireHistorical.allColumns', 'All Columns')}</MenuItem>
              <MenuItem value="serial_number">{t('tireRepairScrap.serialNumberExport', 'Serial Number')}</MenuItem>
              <MenuItem value="license_plate">{t('tireHistorical.licensePlate', 'License Plate')}</MenuItem>
              <MenuItem value="transaction_type">{t('tireCardHistory.actionType', 'Transaction Type')}</MenuItem>
            </Select>

            {searchColumn === 'transaction_type' ? (
              <Select
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                displayEmpty
                sx={{ width: { xs: '100%', sm: 200 }, bgcolor: 'white', height: 40 }}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">
                    {t('tireHistorical.selectActionType', 'Select Action Type')}
                  </Typography>
                </MenuItem>
                <MenuItem value="FIT">FIT</MenuItem>
                <MenuItem value="REMOVE">REMOVE</MenuItem>
                <MenuItem value="INSPECTION">INSPECTION</MenuItem>
                <MenuItem value="REPAIR">REPAIR</MenuItem>
                <MenuItem value="SCRAP">SCRAP</MenuItem>
              </Select>
            ) : (
              <TextField
                size="small"
                placeholder={t('tireRepairScrap.search', 'Search...')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
                sx={{ width: { xs: '100%', sm: 200 }, '& .MuiInputBase-root': { height: 40 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            )}
            <Button
              variant="contained"
              size="medium"
              disableElevation
              onClick={handleSearchClick}
              sx={{ minWidth: 'auto', textTransform: 'none', height: 40 }}
            >
              {t('tireRepairScrap.search', 'Search')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <HtmlTableContainer>
        <HtmlTable style={{ minWidth: 1200 }}>
          <thead>
            <tr>
              <StyledTableCell
                style={{
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 3,
                  width: 160,
                  minWidth: 160,
                  maxWidth: 160,
                  boxSizing: 'border-box'
                }}
              >
                {t('tireRepairScrap.serialNumberExport', 'Serial Number')}
              </StyledTableCell>
              <StyledTableCell
                style={{
                  position: 'sticky',
                  left: 160,
                  top: 0,
                  zIndex: 3,
                  width: 140,
                  minWidth: 140,
                  maxWidth: 140,
                  boxSizing: 'border-box'
                }}
              >
                {t('tireRepairScrap.date', 'Date')}
              </StyledTableCell>
              <StyledTableCell
                style={{
                  position: 'sticky',
                  left: 300,
                  top: 0,
                  zIndex: 3,
                  width: 140,
                  minWidth: 140,
                  maxWidth: 140,
                  boxSizing: 'border-box',
                  borderRight: '1px solid #e2e8f0'
                }}
              >
                {t('tireCardHistory.actionType', 'Action Type')}
              </StyledTableCell>
              <StyledTableCell>{t('tireHistorical.specification', 'Specification')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.vehicle', 'Vehicle')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.kmUnit', 'KM Unit')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.hmUnit', 'HM Unit')}</StyledTableCell>
              <StyledTableCell>{t('tireCycle.position', 'Position')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)')}</StyledTableCell>
              <StyledTableCell>{t('tireCardHistory.mechanicName', 'Mechanic Name')}</StyledTableCell>
              <StyledTableCell align="center">{t('detail', 'Detail')}</StyledTableCell>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="12" align="center" style={{ padding: '48px 0' }}>
                  <CircularProgress size={32} thickness={5} />
                </td>
              </tr>
            ) : !hasSearched ? (
              <tr>
                <td colSpan="12" align="center" style={{ padding: '48px 0' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('tireHistorical.pleaseSelectFilters', 'Please select filters and click Search.')}
                  </Typography>
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan="12" align="center" style={{ padding: '48px 0' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('tireHistorical.noTransactionsFound', 'No transactions found.')}
                  </Typography>
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const actionStyles = getActionStyles(row.transaction_type);
                const hm = row.hm_unit != null ? row.hm_unit : 0;
                const km = row.km_unit != null ? row.km_unit : 0;

                return (
                  <StyledTableRow key={row.id || index}>
                    <td
                      style={{
                        fontWeight: 600,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: 'inherit',
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        boxSizing: 'border-box'
                      }}
                    >
                      {row.serial_number || '-'}
                    </td>
                    <td
                      style={{
                        position: 'sticky',
                        left: 160,
                        zIndex: 1,
                        backgroundColor: 'inherit',
                        width: 140,
                        minWidth: 140,
                        maxWidth: 140,
                        boxSizing: 'border-box'
                      }}
                    >
                      {row.transaction_date ? dayjs(row.transaction_date).format('DD MMM YYYY') : '-'}
                    </td>
                    <td
                      style={{
                        position: 'sticky',
                        left: 300,
                        zIndex: 1,
                        backgroundColor: 'inherit',
                        width: 140,
                        minWidth: 140,
                        maxWidth: 140,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #e2e8f0'
                      }}
                    >
                      <Chip
                        label={row.transaction_type || '-'}
                        size="small"
                        sx={{
                          bgcolor: actionStyles.bg,
                          color: actionStyles.color,
                          fontWeight: 700,
                          borderRadius: 1.5,
                          letterSpacing: 0.5
                        }}
                      />
                    </td>
                    <td>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2">{row.tire_spec_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.tire_brand_name || '-'} - {row.tire_size_code_internal || row.tire_size_code || '-'}
                        </Typography>
                      </Box>
                    </td>
                    <td>{row.license_plate || '-'}</td>
                    <td>{Number(km).toLocaleString('id-ID')}</td>
                    <td>{Number(hm).toLocaleString('id-ID')}</td>
                    <td>{findPositionLabel(row.current_position, row.serial_number)}</td>
                    <td>{row.air_pressure != null ? row.air_pressure : '-'}</td>
                    <td>{row.tread_depth != null ? row.tread_depth : '-'}</td>
                    <td>{row.mechanic_name || '-'}</td>
                    <td align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedRow(row);
                          setDetailModalOpen(true);
                        }}
                        sx={{ textTransform: 'none', minWidth: '80px' }}
                      >
                        {t('detail', 'Detail')}
                      </Button>
                    </td>
                  </StyledTableRow>
                );
              })
            )}
          </tbody>
        </HtmlTable>
      </HtmlTableContainer>

      <Box sx={{ borderTop: 'none', bgcolor: '#ffffff', borderRadius: '0 0 8px 8px', border: '1px solid #e2e8f0', mt: 0 }}>
        <TablePagination
          component="div"
          count={totalRecord || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>

      <TransactionDetailModal open={detailModalOpen} onClose={() => setDetailModalOpen(false)} data={selectedRow} />
    </Box>
  );
}
